import { useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { getDateFormat } from "@/utils/date-format";

import EquipmentColumnDefs from "../EquipmentColumnDefs";
import ForensicEquipmentAPI from "@/api/equipment/index.js";
import { useDebouncedCallback } from "use-debounce";
import { MONOLITH_PERMISSIONS } from "@/constants.js";
import {
  RuleGroup,
  Table,
  TableInstance,
} from "@monolith-forensics/monolith-ui";

import EquipmentFilterDefs from "../EquipmentFilterDefs";
import { ForensicEquipment } from "@/types";
import { CreateEquipmentModal } from "../Modals/CreateEquipmentModal";
import { EquipmentFlyout } from "../Flyouts/EquipmentFlyout";
import { resolveFilterState, useTableState } from "@/Features/_shared";

interface EquipmentQuery {
  filter?: RuleGroup;
  page?: number;
  page_size?: number;
  search?: string | null;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
}

// Not "equipment:view": the pre-July-2025 Equipment grid persisted under that
// key (removed in 6e9b4464), so long-time users still have year-old blobs
// there. A versioned key starts them from the current defaults instead, the
// same way StorageListPage moved to "storage:view:v2".
const EquipmentTable = ({ stateStoreKey = "equipment:view:v2" }) => {
  const defaultQueryKey = ["equipment", "list"];
  const pageSize = 100;

  const { hasPermission } = usePermissions();
  const tableInstanceRef = useRef<TableInstance>();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const [flyoutData, setFlyoutData] = useState<ForensicEquipment | null>(null);
  const filterDefinitions = useMemo(() => EquipmentFilterDefs, []);

  const tableState = useTableState({}, stateStoreKey);

  const query: EquipmentQuery = {
    filter: tableState.filterState,
    search: tableState.search || null,
    sort_by: tableState.sortState?.dataField,
    sort_dir: tableState.sortState?.dir,
    page_size: pageSize,
  };

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading,
    isPending,
    isFetchingNextPage,
    isFetching,
    isFetched,
  } = useInfiniteQuery({
    queryKey: [defaultQueryKey, query],
    queryFn: ({ pageParam }) => {
      query.page = pageParam;
      return ForensicEquipmentAPI.getEquipmentItems(query);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page - 1 === 0) return null;
      return firstPage.page - 1;
    },
    initialPageParam: 1,
    enabled: !!query,
    placeholderData: (data) => data,
  });

  const debouncedFetchNextPage = useDebouncedCallback(fetchNextPage, 100);

  const records =
    infiniteData?.pages.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const totalRecords = infiniteData?.pages?.[0]?.total || 0;

  const handleSearch = (searchText: string) => {
    tableState.setSearchState(searchText);
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 250);

  const handleExportTable = (option: string) => {
    let columns = (tableInstanceRef.current?.columnState ?? [])
      .filter((c) => c.dataField !== "Action")
      .map((c) => ({
        dataField: c.dataField,
        header: c.caption || c.dataField,
        caption: c.caption || c.dataField,
        visible: c.visible,
        dataType: c.dataType,
      }));

    if (option === "export:visible") {
      columns = columns.filter((c) => c.visible !== false);
    }

    ForensicEquipmentAPI.exportEquipmentList({
      query,
      type: "xlsx",
      columns,
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );
      el.download = filename;
      el.click();
      // remove snackbar
      el.remove();
    });
  };
  const handleGetNextItem = (id?: number) => {
    const current = records?.findIndex(
      (i: ForensicEquipment) => i.item_id === id
    );
    const nextItem = records[current + 1] || records[0];

    setFlyoutData(nextItem);
  };
  const handleGetPrevItem = (id?: number) => {
    const current = records?.findIndex(
      (i: ForensicEquipment) => i.item_id === id
    );
    if (current === 0) return;
    const prevItem = records[current - 1] || records[records.length - 1];

    setFlyoutData(prevItem);
  };
  const handleRefetchQuery = () => {
    refetch();
  };
  const onCreate = () => handleRefetchQuery();
  const onEdit = () => handleRefetchQuery();
  const onDelete = () => handleRefetchQuery();

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0, //height can't shrink below 0
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        <Table
          tableInstanceRef={tableInstanceRef}
          data={records}
          loading={
            isLoading ||
            isPending ||
            isFetchingNextPage ||
            (!isFetched && isFetching)
          }
          columnProps={EquipmentColumnDefs}
          keyField="item_id"
          virtualized={true}
          enableSorting={true}
          enableActionButton={true}
          totalRecords={totalRecords}
          onActionButtonClick={(item) => {
            setFlyoutData(item);
            setIsFlyoutVisible(true);
          }}
          enableColumnReorder={true}
          enableColumnResize={true}
          manualSorting={true}
          manualFiltering={true}
          manualSearch={true}
          manualExport={true}
          onScrollToBottom={() => {
            if (hasNextPage) {
              debouncedFetchNextPage();
            }
          }}
          onSort={(e) => {
            tableState.setSortState(e.sort || undefined);
          }}
          stateStorage={{
            enabled: !!stateStoreKey,
            type: "localStorage",
            key: stateStoreKey,
          }}
          tableMenuOptions={{
            enabled: true,
            addButtonOptions: {
              enabled: hasPermission(MONOLITH_PERMISSIONS.EQUIPMENT_CREATE),
              label: "New Equipment",
              onClick: () => {
                setShowCreateModal(true);
              },
            },
            exportOptions: {
              enabled: true,
              onExport: handleExportTable,
            },
            columnSelectorOptions: {
              enabled: true,
            },
            tableCountOptions: {
              recordsTotalEnabled: true,
            },
            searchOptions: {
              enabled: true,
              onSearch: debouncedSearch,
              placeholder: "Search Equipment...",
            },
            filterOptions: {
              enabled: true,
              filterDefinitions: filterDefinitions,
              onFilterChange: (filterQuery) => {
                tableState.setFilterState(resolveFilterState(filterQuery));
              },
            },
          }}
        />
      </div>
      {isFlyoutVisible && flyoutData && (
        <EquipmentFlyout
          show={isFlyoutVisible}
          defaultInfo={flyoutData}
          onEdit={onEdit}
          onDelete={() => {
            setIsFlyoutVisible(false);
            setFlyoutData(null);
            onDelete();
          }}
          onClose={() => {
            setIsFlyoutVisible(false);
            setFlyoutData(null);
          }}
          onNext={() => handleGetNextItem(flyoutData?.item_id)}
          onPrevious={() => handleGetPrevItem(flyoutData?.item_id)}
        />
      )}
      <CreateEquipmentModal
        show={showCreateModal}
        onSubmit={onCreate}
        onClose={() => setShowCreateModal(false)}
        onCancel={() => setShowCreateModal(false)}
      />
    </>
  );
};

export default EquipmentTable;
